import React, {
  Fragment, useRef, useState,
} from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { KeyValuePairAny } from 'data/types';
import {
  TargetModel,
  TargetModelAccessFunction,
  TargetModelAccessFunctions,
  TargetModelAfCCC,
  TargetModelAfCCCSettings,
} from 'data/target/target.types';
import { CaseModel } from 'data/case/case.types';
import {
  FormTemplate, FormTemplateField, FormTemplateFieldMap, FormTemplateSection, FormTemplateSectionMetadataLayout,
} from 'data/template/template.types';
import { TargetDeliveryTemplateAccessFunction, TargetDeliveryTemplateAccessFunctionFormData } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { getTemplateOptions } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.adapter';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import Form, { ChangedField } from '../../../../shared/form';
import genbandTemplate, { GENBAND_TARGET_CCC_ROWS } from '../target-ccc-genband';
import './genband-c20-target-ccc.scss';
import { TargetInfo, TargetInfoHashItem, TARGET_INFO_TYPE } from '../target-info-defs';

interface GenbandTargetCCCProps {
  handleUpdate: (afSettings: TargetModelAfCCCSettings[]) => TargetModel[];
  handleTargetInfoHashItemUpdate: (targetInfoHashItem: TargetInfoHashItem) => TargetModel[];
  handleSubmit: (targets: TargetModel[]) => void;
  targetInfoHashItem: TargetInfoHashItem | null;
  afDetails: TargetModelAccessFunction;
  afid: string;
  currentStateMode: any;
  targetId: string;
  caseId: string;
  targetType: string;
  selectedAfs: { afs: TargetModelAccessFunctions }
  afDIDetails: any;
  warrantCase: CaseModel;
  targetDeliveryTemplates: TargetDeliveryTemplateAccessFunction[];
  targetDeliveryTemplatesMap: { [key: string]: TargetDeliveryTemplateAccessFunctionFormData };
}

function getAfCCCInfo(afccc: TargetModelAfCCC[], afDetailsType?: string): TargetModelAfCCC | undefined {
  return afccc?.find((af: any) => af.afType === afDetailsType);
}

function adaptTargetCCCForm(
  warrantCase: CaseModel,
  afccc?: TargetModelAfCCC[],
  afDetailsType?: string,
  afId?: string,
  currentStateMode?: CONFIG_FORM_MODE,
  selectedAfs?: { afs: TargetModelAccessFunctions },
  afid?: string,
  targetId?: string,
  targetDeliveryTemplates?: TargetDeliveryTemplateAccessFunction[],
  afTag?: string,
): FormTemplate {
  let clonedTemplate = cloneDeep(genbandTemplate);

  const templateOptions = getTemplateOptions(targetDeliveryTemplates || [], afTag || '');

  (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.options = [
    { value: '', label: 'Select a template' },
    ...templateOptions,
  ];

  if (
    warrantCase == null ||
    afccc == null ||
    afDetailsType == null ||
    afId == null ||
    currentStateMode == null ||
    selectedAfs == null
  ) {
    return clonedTemplate;
  }

  const afCCCInfo = getAfCCCInfo(afccc, afDetailsType);

  if (afCCCInfo == null) {
    return clonedTemplate;
  }

  const cccOptions = afCCCInfo.afSettings.find((setting) => setting.afids[0] === afId)?.options
    .reduce((total, option) => ({
      ...total,
      [option.key]: option.value,
    }), {}) as { [key: string]: string | number | boolean | string[] };

  if (cccOptions == null) {
    return clonedTemplate;
  }

  Object.keys((clonedTemplate.callContentChannels as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.callContentChannels as FormTemplateSection).fields[key] != null) {
      if (cccOptions?.[key] != null) {
        (clonedTemplate.callContentChannels as FormTemplateSection).fields[key].initial = cccOptions?.[key];
      }
      (clonedTemplate.callContentChannels as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }

  });

  Object.keys((clonedTemplate.general as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.general as FormTemplateSection).fields[key] != null) {
      if (cccOptions?.[key] != null) {
        (clonedTemplate.general as FormTemplateSection).fields[key].initial = cccOptions?.[key];
      }
      (clonedTemplate.general as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }
  });

  const accessFunctionIndex = selectedAfs.afs.afIds.findIndex(((af) => af.id === afid));
  const clonedWarrantCase = cloneDeep(warrantCase);

  const clonedTargetInfoHash = (clonedWarrantCase.targets || [])
    .find((target) => target.id === targetId)?.targetInfoHash
    ?.find((target: any) => target.targetInfoType === TARGET_INFO_TYPE.NTILITARGETINFO);

  if (clonedTargetInfoHash != null && clonedTargetInfoHash.targetInfos != null) {

    const targetOptions = (clonedTargetInfoHash.targetInfos[accessFunctionIndex]?.options || [])
      .reduce((total, option) => ({
        ...total,
        [option.key]: option.value,
      }), {}) as { [key: string]: string | number | boolean | string[] };

    Object.keys((clonedTemplate.targetInformation as FormTemplateSection).fields).forEach((key: string) => {

      if ((clonedTemplate.targetInformation as FormTemplateSection).fields[key] != null) {
        if (targetOptions?.[key] != null) {
          (clonedTemplate.targetInformation as FormTemplateSection).fields[key].initial = targetOptions?.[key];
        }
        (clonedTemplate.targetInformation as FormTemplateSection).fields[key].readOnly =
          currentStateMode === CONFIG_FORM_MODE.View;
      }

      if ((clonedTemplate.targetInformation as FormTemplateSection).fields.GRPID != null) {
        (clonedTemplate.targetInformation as FormTemplateSection).fields.GRPID.initial = cccOptions?.GRPID;
        (clonedTemplate.targetInformation as FormTemplateSection).fields.GRPID.readOnly =
          currentStateMode === CONFIG_FORM_MODE.View;
      }

      if ((clonedTemplate.callContentChannels as FormTemplateSection).fields.PREPENDRTEDIGS != null) {
        (clonedTemplate.callContentChannels as FormTemplateSection).fields.PREPENDRTEDIGS.initial =
          targetOptions?.PREPENDRTEDIGS;
        (clonedTemplate.callContentChannels as FormTemplateSection).fields.PREPENDRTEDIGS.readOnly =
          currentStateMode === CONFIG_FORM_MODE.View;
        (clonedTemplate.callContentChannels as FormTemplateSection).fields.NUMRTEDIGS.initial =
          targetOptions?.NUMRTEDIGS;
        (clonedTemplate.callContentChannels as FormTemplateSection).fields.NUMRTEDIGS.readOnly =
          currentStateMode === CONFIG_FORM_MODE.View;
      }
    });
  }

  clonedTemplate = updateTemplateFieldsValues(clonedTemplate);

  clonedTemplate.formId = Date.now().toString();
  return clonedTemplate;
}

function enableField(
  field: FormTemplateField,
  shouldEnable: boolean,
): void {

  if (shouldEnable) {
    field.hide = 'false';
    field.disabled = false;
  }
}

function updateRows(
  targetInformationAAA: string,
  section: 'targetInformation' | 'callContentChannels',
): string[][] {

  const key = targetInformationAAA as ('IBN' | 'BRI' | 'PRI' | 'MFT' | 'VA' | 'HNT' | 'TRK');

  const newRows: string[][] = GENBAND_TARGET_CCC_ROWS?.[section]?.[key];

  if (newRows == null) {
    return [[]];
  }

  return newRows;
}

function disableField(
  field: FormTemplateField,
): void {
  field.hide = 'true';
  field.disabled = true;
}

function updateTemplateFieldsValues(inputTemplate: FormTemplate) {
  const template = cloneDeep(inputTemplate);

  const targetInformationAAA = (template.targetInformation as FormTemplateSection).fields.ACC.initial;

  if (targetInformationAAA == null) {
    return template;
  }
  template.key = Date.now().toString();
  if (['PRI', 'TRK', 'BRI', 'VA'].includes(targetInformationAAA as string)) {
    enableField(
      (template.targetInformation as FormTemplateSection).fields.VARIANT,
      targetInformationAAA === 'PRI' || targetInformationAAA === 'TRK',
    );
    enableField(
      (template.callContentChannels as FormTemplateSection).fields.PREPENDRTEDIGS,
      targetInformationAAA === 'PRI' || targetInformationAAA === 'TRK',
    );

    enableField(
      (template.targetInformation as FormTemplateSection).fields.LTID,
      targetInformationAAA === 'PRI' || targetInformationAAA === 'BRI',
    );

    enableField(
      (template.callContentChannels as FormTemplateSection).fields.MAXCCC,
      targetInformationAAA === 'TRK' || targetInformationAAA === 'VA',
    );

    enableField(
      (template.targetInformation as FormTemplateSection).fields.FDCP,
      targetInformationAAA === 'PRI',
    );
    enableField(
      (template.targetInformation as FormTemplateSection).fields.CLLI,
      targetInformationAAA === 'PRI',
    );

    enableField(
      (template.targetInformation as FormTemplateSection).fields.CGNDN,
      targetInformationAAA === 'PRI',
    );

    enableField(
      (template.targetInformation as FormTemplateSection).fields.CDNDN,
      targetInformationAAA === 'PRI',
    );
  } else {
    disableField((template.targetInformation as FormTemplateSection).fields.VARIANT);
    disableField((template.targetInformation as FormTemplateSection).fields.FDCP);
    disableField((template.targetInformation as FormTemplateSection).fields.CLLI);
    disableField((template.targetInformation as FormTemplateSection).fields.CGNDN);
    disableField((template.targetInformation as FormTemplateSection).fields.CDNDN);
    disableField((template.callContentChannels as FormTemplateSection).fields.PREPENDRTEDIGS);
    disableField((template.callContentChannels as FormTemplateSection).fields.MAXCCC);

    if (targetInformationAAA === 'HNT') {
      enableField(
        (template.targetInformation as FormTemplateSection).fields.LEN,
        targetInformationAAA === 'HNT',
      );
    } else {
      disableField((template.targetInformation as FormTemplateSection).fields.LEN);
    }
  }

  ((template.callContentChannels as FormTemplateSection).fields.NUMRTEDIGS.disabled) =
    (template.callContentChannels as FormTemplateSection).fields.PREPENDRTEDIGS.initial === 'NO';

  ((template.callContentChannels as FormTemplateSection).fields.SAN.disabled) =
    (template.callContentChannels as FormTemplateSection).fields.SANREQ.initial === 'NO' || true;

  if ((template?.targetInformation as FormTemplateSection)?.metadata?.layout?.rows != null) {

    (
      (template.targetInformation as FormTemplateSection).metadata.layout as FormTemplateSectionMetadataLayout
    ).rows = updateRows(
      targetInformationAAA as string,
      'targetInformation',
    );
  }

  if ((template.callContentChannels as FormTemplateSection)?.metadata?.layout?.rows != null) {
    (
      (template.callContentChannels as FormTemplateSection).metadata.layout as FormTemplateSectionMetadataLayout
    ).rows = updateRows(
      targetInformationAAA as string,
      'callContentChannels',
    );
  }

  return template;

}

function adaptSectionFields(
  fields: any,
  section: 'targetInformation' | 'callContentChannels',
  rows?: string[][],
): any {
  const clonedFields = cloneDeep(fields);
  if (rows == null) {
    return clonedFields;
  }

  Object.keys(fields).forEach((key) => {
    const isVisible = rows.some((row) => row.includes(key));
    if (!isVisible) {
      clonedFields[key] = undefined;
    }
  });
  return clonedFields;
}

function getDefaultData(filedSection: FormTemplateFieldMap) {
  return Object.keys(filedSection).reduce((total, key) => ({
    ...total,
    [key]: filedSection[key].initial,
  }), {});
}

const GenbandC20TargetCCC = ({
  afDetails,
  handleUpdate,
  handleSubmit,
  afid,
  currentStateMode,
  targetId,
  selectedAfs,
  warrantCase,
  targetDeliveryTemplates,
  targetDeliveryTemplatesMap,
}: GenbandTargetCCCProps) => {

  const [template, setTemplate] = useState<FormTemplate>(genbandTemplate);
  const generalRef = useRef<Form>(null);
  const targetInformationRef = useRef<Form>(null);
  const callContentChannelsRef = useRef<Form>(null);
  const { t } = useTranslation();

  function handleFieldUpdate({ name, value }: ChangedField) {
    const clonedTemplate = cloneDeep(template);

    let targetInformationFields: FormData;
    let callContentChannelsFields: FormData;
    let isTargetInformationValid;
    let isCallContentChannelsValid;

    if (name === 'targetDeliveryTemplate') {
      const selectedTemplate = cloneDeep(targetDeliveryTemplatesMap[value]);

      targetInformationFields =
        cloneDeep(selectedTemplate?.targetInformation?.[0]) || targetInformationRef?.current?.getValues();
      callContentChannelsFields =
        cloneDeep(selectedTemplate?.callContentChannels?.[0]) || callContentChannelsRef?.current?.getValues();

      isTargetInformationValid = true;
      isCallContentChannelsValid = true;
    } else {
      targetInformationFields = targetInformationRef?.current?.getValues();
      callContentChannelsFields = callContentChannelsRef?.current?.getValues();

      isTargetInformationValid = targetInformationRef?.current?.isValid(true, true);
      isCallContentChannelsValid = callContentChannelsRef?.current?.isValid(true, true);
    }

    const generalFields: FormData = generalRef?.current?.getValues();

    adaptSectionFields(
      cloneDeep(targetInformationFields),
      'targetInformation',
      (template.targetInformation as FormTemplateSection).metadata.layout?.rows,
    );

    adaptSectionFields(
      cloneDeep(callContentChannelsFields),
      'callContentChannels',
      (template.callContentChannels as FormTemplateSection).metadata.layout?.rows,
    );

    const allFields: FormData = { ...generalFields, ...targetInformationFields, ...callContentChannelsFields };

    const isValid = generalRef?.current?.isValid(true, true)
      && isTargetInformationValid
      && isCallContentChannelsValid;

    const options: KeyValuePairAny[] = [];

    const targetInfo: TargetInfo = {
      ifid: '',
      options: [],
    };
    if (allFields) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Object.entries(allFields).forEach((entry: [string, any]) => {
        if (entry[1] === true) {
          entry[1] = 'YES';
        } else if (entry[1] === false) {
          entry[1] = 'NO';
        } else if (entry[1] === 'true') {
          entry[1] = 'YES';
        } else if (entry[1] === 'false') {
          entry[1] = 'NO';
        }

        if ((entry[0] === 'LIID' || entry[0] === 'ACC' || entry[0] === 'VARIANT'
          || entry[0] === 'LTID' || entry[0] === 'CDNDN' || entry[0] === 'PREPENDRTEDIGS'
          || entry[0] === 'NUMRTEDIGS' || entry[0] === 'CDCTGTDN' || entry[0] === 'MONOMODE'
          || entry[0] === 'CLLI' || entry[0] === 'LEN' || entry[0] === 'MAXCCC' || entry[0] === 'MAXCCC'
          || entry[0] === 'FDCP' || entry[0] === 'PNI' || entry[0] === 'FDCP' || entry[0] === 'CGNDN') && entry[1]) {

          targetInfo.options.push({ key: entry[0], value: entry[1] });
        } else if (entry[1]) {
          options.push({ key: entry[0], value: entry[1] });
        }
      });
    }

    const accessFunctionIndex = selectedAfs.afs.afIds.findIndex(((af) => af.id === afid));
    const clonedWarrantCase = cloneDeep(warrantCase);

    const clonedTargetInfoHash = (clonedWarrantCase.targets || [])
      .find((target) => target.id === targetId)?.targetInfoHash
      ?.find((target: any) => target.targetInfoType === TARGET_INFO_TYPE.NTILITARGETINFO);

    const targetInfoHash: TargetInfoHashItem[] = [{
      targetInfoType: TARGET_INFO_TYPE.NTILITARGETINFO,
      targetInfos: clonedTargetInfoHash?.targetInfos || [],
    }];

    targetInfoHash[0].targetInfos[accessFunctionIndex] = targetInfo;

    const afCCCSettings: TargetModelAfCCCSettings[] = [{
      afids: [afDetails.id as string],
      isValid,
      options,
      autowired: false,
    }];

    const afs = handleUpdate(afCCCSettings);

    const targets = afs?.map((af) => {
      if (af.id === targetId) {
        return { ...af, targetInfoHash };
      }
      return { ...af };
    });

    handleSubmit(targets);
    clonedTemplate.key = Date.now().toString();
    setTemplate(clonedTemplate);
  }

  return (
    afDetails
      ? (
        <Fragment>
          <Form
            name={template.key}
            fields={
              (adaptTargetCCCForm(
                warrantCase,
                selectedAfs?.afs?.afccc,
                afDetails.type,
                afid,
                currentStateMode,
                selectedAfs,
                afid,
                targetId,
                targetDeliveryTemplates,
                afDetails.tag,
              ).general as FormTemplateSection).fields
            }
            layout={
              (adaptTargetCCCForm(
                warrantCase,
                selectedAfs?.afs?.afccc,
                afDetails.type,
                afid,
                currentStateMode,
                selectedAfs,
                afid,
                targetId,
                targetDeliveryTemplates,
                afDetails.tag,
              )?.general as FormTemplateSection).metadata?.layout?.rows
            }
            ref={generalRef}
            flexFlow="row"
            fieldUpdateHandler={handleFieldUpdate}
            i18nKeyPrefix="warrant.target.form"
          />
          <div className="subsection-header">
            {t('warrant.target.form.genband.targetInformation')}
          </div>
          <Form
            shouldUpdateRows
            name={template.key}
            defaults={
              getDefaultData(
                (adaptTargetCCCForm(
                  warrantCase,
                  selectedAfs?.afs?.afccc,
                  afDetails.type,
                  afid,
                  currentStateMode,
                  selectedAfs,
                  afid,
                  targetId,
                  targetDeliveryTemplates,
                ).targetInformation as FormTemplateSection).fields,
              )
            }
            fields={
              (adaptTargetCCCForm(
                warrantCase,
                selectedAfs?.afs?.afccc,
                afDetails.type,
                afid,
                currentStateMode,
                selectedAfs,
                afid,
                targetId,
                targetDeliveryTemplates,
              ).targetInformation as FormTemplateSection).fields
            }
            layout={
              (adaptTargetCCCForm(
                warrantCase,
                selectedAfs?.afs?.afccc,
                afDetails.type,
                afid,
                currentStateMode,
                selectedAfs,
                afid,
                targetId,
                targetDeliveryTemplates,
              )?.targetInformation as FormTemplateSection).metadata?.layout?.rows
            }
            ref={targetInformationRef}
            flexFlow="row"
            fieldUpdateHandler={handleFieldUpdate}
            i18nKeyPrefix="warrant.target.form"
          />
          <div className="subsection-header">
            {t('warrant.target.form.genband.callContentChannels')}
          </div>
          <Form
            shouldUpdateRows
            name={template.key}
            defaults={
              getDefaultData(
                (adaptTargetCCCForm(
                  warrantCase,
                  selectedAfs?.afs?.afccc,
                  afDetails.type,
                  afid,
                  currentStateMode,
                  selectedAfs,
                  afid,
                  targetId,
                  targetDeliveryTemplates,
                ).callContentChannels as FormTemplateSection).fields,
              )
            }
            fields={
              (adaptTargetCCCForm(
                warrantCase,
                selectedAfs?.afs?.afccc,
                afDetails.type,
                afid,
                currentStateMode,
                selectedAfs,
                afid,
                targetId,
                targetDeliveryTemplates,
              ).callContentChannels as FormTemplateSection).fields
            }
            layout={
              (adaptTargetCCCForm(
                warrantCase,
                selectedAfs?.afs?.afccc,
                afDetails.type,
                afid,
                currentStateMode,
                selectedAfs,
                afid,
                targetId,
                targetDeliveryTemplates,
              )?.callContentChannels as FormTemplateSection).metadata?.layout?.rows
            }
            ref={callContentChannelsRef}
            flexFlow="row"
            fieldUpdateHandler={handleFieldUpdate}
            i18nKeyPrefix="warrant.target.form"
          />
        </Fragment>
      )
      :
        <Fragment />
  );
};

const mapStateToProps = ({
  warrants: { changeTracker },
}: any) => ({
  changeTracker,
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(GenbandC20TargetCCC);
